/**
 * Reverse of mapPolicyPayload.ts — transforms DataAccessPolicy API responses
 * back into ConditionBuilder form values for populating the wizard in edit mode.
 *
 * Pure TypeScript, no React/side effects.
 */
import {
  type DataAccessPolicyRuleColumns,
  type DataAccessPolicyWithoutUDFModel,
} from "@/api";
import {
  type ConditionAttributeOption,
  type ConditionEntry,
  type ConditionOption,
  type MatchWithOption,
} from "@/components/ConditionBuilder";
import {
  extractInnerRules,
  reconstructConditionValue,
  resolveConditionAttributeOption,
} from "@/helpers/policyColumnRules";
import { type RegisterMcpFormValues } from "@/pages/MCPRegistry/mcpServerDialogTypes";
import {
  type ResourcePolicyDraft,
  type ResourcePolicyScope,
} from "@/pages/MCPRegistry/resourcePolicies/types";
import {
  MATCH_WITH_DEFAULT_NAME,
  buildMcpMatchWithOption,
} from "./matchWithOptions";
import { CONDITION_ID_TO_OPERATOR_OPTION } from "./operatorMappings";

// ─── Reverse-lookup constants ─────────────────────────────────────────────────

const FALLBACK_OPERATOR: ConditionOption = { id: "equals", name: "Equals" };

// ─── Attribute resolution ─────────────────────────────────────────────────────

/**
 * Maps API elementType to the ConditionBuilder group ID.
 * Inverse of mapElementType() in mapPolicyPayload.ts.
 */
function elementTypeToGroupId(elementType?: string): "AGENT" | "USER" {
  return elementType === "AGENT_ATTRIBUTE" ? "AGENT" : "USER";
}

/**
 * Resolves a full ConditionAttributeOption by looking up elementName (the
 * attribute's display name) in the live attributes list from the API.
 * Falls back to a minimal object when no match is found.
 */
function resolveAttribute(
  elementName: string | undefined,
  elementType: string | undefined,
  attributes: ConditionAttributeOption[],
): ConditionAttributeOption {
  return resolveConditionAttributeOption({
    elementName,
    elementType,
    attributes,
    groupIdFor: elementTypeToGroupId,
    // Group ID, so ConditionBuilder can still place an unresolved attribute in
    // the correct hierarchical group.
    fallbackType: elementTypeToGroupId(elementType),
  });
}

// ─── Rule → ConditionEntry conversion ────────────────────────────────────────

/**
 * Recursively converts a single DataAccessPolicyRuleColumns node into a
 * ConditionEntry (leaf or group).
 */
function columnRuleToConditionEntry(
  rule: DataAccessPolicyRuleColumns,
  genId: () => string,
  attributes: ConditionAttributeOption[],
): ConditionEntry {
  // Group: has nested columnRules with content
  if (rule.columnRules && rule.columnRules.length > 0) {
    return {
      id: genId(),
      combinator: rule.conjunction === "AND" ? "AND" : "OR",
      conditions: rule.columnRules.map(r =>
        columnRuleToConditionEntry(r, genId, attributes),
      ),
    };
  }

  // Leaf: individual condition
  const attr = resolveAttribute(rule.elementName, rule.elementType, attributes);
  // Reconstruct the Match With selection from the persisted `valueType` so edit
  // mode is ungated and the value field renders the right control. The label is
  // the untranslated default; the live resolveOperator reconciles name + full
  // fetch config once the attribute resolves.
  const matchWith: MatchWithOption | null = rule.valueType
    ? buildMcpMatchWithOption(rule.valueType, {
        attributeId: attr.id,
        isMultiValue: attr.valueType === "multi-select",
        name: MATCH_WITH_DEFAULT_NAME[rule.valueType],
      })
    : null;
  return {
    id: genId(),
    attribute: attr,
    operator:
      CONDITION_ID_TO_OPERATOR_OPTION[rule.conditionId ?? ""] ??
      FALLBACK_OPERATOR,
    matchWith,
    value: reconstructConditionValue(rule.value, matchWith, attr),
  };
}

/**
 * Saved column rules back into ConditionBuilder entries. `attributes` resolves
 * each leaf's attribute; without them it falls back to one named after the
 * stored `elementName`, which the builder reconciles once its config loads.
 */
export const columnRulesToConditionEntries = (
  rules: DataAccessPolicyRuleColumns[],
  genId: () => string,
  attributes: ConditionAttributeOption[] = [],
): ConditionEntry[] =>
  rules.map(rule => columnRuleToConditionEntry(rule, genId, attributes));

/**
 * One policy's own condition tree, kept separate from any other policy on the
 * same tool — `mapPoliciesToFormValues` merges a tool's policies into one rule
 * set for the editor, which a read-only view must not do.
 */
export function policyToConditions(
  policy: DataAccessPolicyWithoutUDFModel,
  genId: () => string,
  attributes: ConditionAttributeOption[],
): { combinator: "AND" | "OR"; entries: ConditionEntry[] } {
  const { combinator, rules } = extractInnerRules<DataAccessPolicyRuleColumns>(
    policy.rules,
  );
  return {
    combinator,
    entries: columnRulesToConditionEntries(rules, genId, attributes),
  };
}

/** The tool a RESOURCE (tool-level) policy is attached to, if it names one. */
export const toolNameOfPolicy = (
  policy: DataAccessPolicyWithoutUDFModel,
): string | undefined =>
  policy.resourceMetadata?.objectNames?.[0] ?? policy.objectNameList?.[0];

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Maps a list of existing DataAccessPolicy API items back to the subset of
 * RegisterMcpFormValues that drives the ConditionBuilder steps.
 *
 * @param policies   - Items from listResourceAccessPoliciesUsingGet response
 * @param genId      - Stable ID generator (e.g. () => crypto.randomUUID())
 * @param attributes - Live attribute list from useMcpConditionBuilderConfig, used to
 *                     resolve elementName back to a full ConditionAttributeOption
 */
export function mapPoliciesToFormValues(
  policies: DataAccessPolicyWithoutUDFModel[],
  genId: () => string,
  attributes: ConditionAttributeOption[],
): Partial<RegisterMcpFormValues> {
  const serverPolicy = policies.find(
    p => p.resourceSourceType === "APPLICATION",
  );
  const toolPolicies = policies.filter(
    p => p.resourceSourceType === "RESOURCE",
  );

  // ── Server-level rules ──────────────────────────────────────────────────────
  let serverRules: ConditionEntry[] = [];
  let serverRulesCombinator: "AND" | "OR" = "OR";

  if (serverPolicy) {
    const { combinator, rules } =
      extractInnerRules<DataAccessPolicyRuleColumns>(serverPolicy.rules);
    serverRulesCombinator = combinator;
    serverRules = rules.map(r =>
      columnRuleToConditionEntry(r, genId, attributes),
    );
  }

  // ── Tool-level rules ────────────────────────────────────────────────────────
  const toolRules: Record<string, ConditionEntry[]> = {};
  const toolRulesCombinator: Record<string, "AND" | "OR"> = {};
  const toolSelected: Record<string, boolean> = {};
  const toolPolicyTypes: Record<string, "inherit" | "custom"> = {};

  for (const policy of toolPolicies) {
    const toolName = toolNameOfPolicy(policy);
    if (!toolName) continue;

    const { combinator, rules } =
      extractInnerRules<DataAccessPolicyRuleColumns>(policy.rules);
    toolRules[toolName] = rules.map(r =>
      columnRuleToConditionEntry(r, genId, attributes),
    );
    toolRulesCombinator[toolName] = combinator;
    toolSelected[toolName] = true;
    toolPolicyTypes[toolName] = "custom";
  }

  return {
    serverRules,
    serverRulesCombinator,
    toolRules,
    toolRulesCombinator,
    toolSelected,
    toolPolicyTypes,
  };
}

// ─── Resource policy → draft conversion ───────────────────────────────────────

/**
 * Inverse of buildResourcePolicyPayload's resourceMetadata encoding
 * (see mapPolicyPayload.ts). This is a provisional adapter, not a
 * backend-defined contract — it will likely need to change once the
 * backend/spec defines a real CONTENT payload shape.
 */
function resourcePolicyScopeFromMetadata(
  metadata: DataAccessPolicyWithoutUDFModel["resourceMetadata"],
): {
  scope: ResourcePolicyScope;
  specificPath?: string;
  resourceTypes: string[];
} {
  if (metadata?.objectType === "ResourcePath") {
    return {
      scope: "specific",
      specificPath: metadata.objectNames?.[0] ?? "",
      resourceTypes: [],
    };
  }
  return {
    scope: "metadata",
    resourceTypes: metadata?.objectNames?.length
      ? metadata.objectNames
      : ["Documents"],
  };
}

/**
 * Maps CONTENT-scoped policies (resourceSourceType "CONTENT" — not yet a
 * member of the generated resourceSourceType enum, see mapPolicyPayload.ts)
 * back into ResourcePolicyDraft[] for the Resource Policies wizard step.
 *
 * Kept separate from mapPoliciesToFormValues because it needs the
 * RESOURCE-attribute-inclusive attribute list (from
 * useMcpConditionBuilderConfig({ includeResourceAttributes: true })), while
 * mapPoliciesToFormValues's existing callers only need USER/AGENT attributes.
 *
 * @param policies   - Items from listResourceAccessPoliciesUsingGet response
 * @param genId      - Stable ID generator (e.g. () => crypto.randomUUID())
 * @param attributes - Live attribute list including the RESOURCE group, used
 *                     to resolve elementName back to a full ConditionAttributeOption
 */
export function mapResourcePoliciesToFormValues(
  policies: DataAccessPolicyWithoutUDFModel[],
  genId: () => string,
  attributes: ConditionAttributeOption[],
): ResourcePolicyDraft[] {
  const contentPolicies = policies.filter(
    p => (p.resourceSourceType as unknown as string) === "CONTENT",
  );

  return contentPolicies.map(policy => {
    const { combinator, rules } =
      extractInnerRules<DataAccessPolicyRuleColumns>(policy.rules);
    const { scope, specificPath, resourceTypes } =
      resourcePolicyScopeFromMetadata(policy.resourceMetadata);
    return {
      // Reuse the id stashed in resourceMetadata.id at save time so the next
      // save round-trips to the same deterministic policy name (update, not
      // duplicate-create). Falls back to a fresh id if metadata is missing.
      id: policy.resourceMetadata?.id ?? genId(),
      name: policy.description || "Untitled Resource Policy",
      scope,
      specificPath,
      resourceTypes,
      conditions: rules.map(r =>
        columnRuleToConditionEntry(r, genId, attributes),
      ),
      combinator,
    };
  });
}
